import { HTMLAttributes, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SCALE } from '../../../common/Image/ImageScale';
import styles from './VideoCard.module.scss';
import PlayButton from '../../../common/PlayButton/PlayButton';
import { VIDEO } from '../../../../lib/constants/courses';
import { CY_COURSE_CARD_ATTRIBUTES } from '../../../../lib/constants/cypress';
import { TRAINING_TYPE_LABEL, VIDEO_LABELS } from '../../../../lib/constants/labelKeys';
import { Label } from '../../../common/Label/Label';
import { useAttributeLabel } from '../../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../../lib/constants/attributes';
import { videoCardIds } from '../../../../lib/tests/testIds';
import { classes } from '../../../../lib/utils/cssUtils';
import { SEARCH_RESULT_CLICK_TRACKING } from '../../../../lib/constants/attributesAnalytics';
import { useFiltersView } from '../../../../lib/context/FiltersViewContext';
import { Api } from '../../../../lib/types/apiSpec';
import LearningLink from '../LearningLink/LearningLink';
import { LearningImage } from '../../../common/Image/LearningImage';
import { PureLinkVariant } from '../../../common/Link/PureLink';
import { DetailsLinkDestination } from '../../../../lib/hooks/useGetDetailsLink';

export type VideoCardProps = {
  classNames?: Partial<Record<'imageWrapper' | 'content', string>>;
  position?: number;
  hideImage?: boolean;
  type: Api.CommonVideoType;
} & Omit<Api.MicrolearningDetails, 'videoId'> &
  Omit<Partial<Api.VideoLibrary>, 'type'> &
  HTMLAttributes<HTMLDivElement>;

const VideoCard = ({
  name,
  imageURL,
  chapters,
  references,
  lastUpdate,
  description,
  id,
  productBrands,
  classNames,
  position,
  hideImage,
  className,
  type,
  productLines,
  ...rest
}: VideoCardProps) => {
  const { withSearch, pageSize } = useFiltersView();
  const location = useLocation();
  const [showAllChapters, setShowAllChapters] = useState(false);
  const chaptersAriaLabel = useAttributeLabel(ATTRIBUTES.ARIA_LABEL, VIDEO_LABELS.CARD.CHAPTERS_ARIA_LABEL);
  const chaptersRefs = useRef<HTMLAnchorElement[]>([]);

  const handleShowAllChapters = () => {
    setShowAllChapters(true);
    setTimeout(() => chaptersRefs.current[VIDEO.CHAPTERS_VISIBLE]?.focus());
  };

  const commonLinkProps = {
    type,
    name,
    id,
    toLitmos: false,
    params: {
      play: 'true',
    },
    ...(withSearch && {
      [SEARCH_RESULT_CLICK_TRACKING.TITLE]: name,
      [SEARCH_RESULT_CLICK_TRACKING.POSITION]: position,
      [SEARCH_RESULT_CLICK_TRACKING.ITEMS]: pageSize,
      [SEARCH_RESULT_CLICK_TRACKING.LOCATION]: `${window.location.origin}${location.pathname}${location.search}${location.hash}`,
    }),
  };

  return (
    <div
      data-testid={videoCardIds.wrapper}
      role="group"
      aria-labelledby={id}
      {...rest}
      className={classes(styles.container, className)}
    >
      {!hideImage && (
        <div className={classes(styles.imageWrapper, classNames?.imageWrapper)}>
          <LearningLink
            {...commonLinkProps}
            className={styles.imageLink}
            aria-hidden
            tabIndex={-1}
            data-testid={videoCardIds.imageLink}
          >
            <PlayButton />
            <LearningImage
              name={name}
              type={type}
              imageURL={imageURL}
              scale={SCALE.GRID}
              className={styles.imageContainer}
            />
          </LearningLink>
        </div>
      )}
      <div className={classes(styles.content, classNames?.content)}>
        <Label
          name={TRAINING_TYPE_LABEL(type)}
          className={styles.label}
          data-cy={CY_COURSE_CARD_ATTRIBUTES.COURSE_TYPE}
        />
        <LearningLink {...commonLinkProps} id={id} className={styles.title}>
          {name}
        </LearningLink>
        <ol className={styles.chaptersList} aria-live="polite" {...chaptersAriaLabel}>
          {chapters?.slice(0, showAllChapters ? chapters.length : VIDEO.CHAPTERS_VISIBLE)?.map((chapter, index) => (
            <li key={chapter.start}>
              <LearningLink
                {...commonLinkProps}
                params={{ startTime: chapter.start?.toString(), ...commonLinkProps.params }}
                className={styles.chapterLink}
                ref={(e: HTMLAnchorElement) => {
                  chaptersRefs.current[index] = e;
                }}
              >
                {chapter.name}
              </LearningLink>
            </li>
          ))}
        </ol>
        {(chapters?.length ?? 0) > VIDEO.CHAPTERS_VISIBLE && !showAllChapters && (
          <button className={styles.showAllChaptersButton} onClick={handleShowAllChapters}>
            <Label name={VIDEO_LABELS.CARD.SHOW_MORE_CHAPTERS} /> ({(chapters?.length ?? 0) - VIDEO.CHAPTERS_VISIBLE})
          </button>
        )}
        {!chapters && (
          <LearningLink
            {...commonLinkProps}
            type={type}
            id={id}
            name={name}
            variant={PureLinkVariant.Link}
            to={DetailsLinkDestination.INTERNAL}
            card
          />
        )}
      </div>
    </div>
  );
};

export default VideoCard;
